import { normalizePermission } from '../../utils/permissionDependencies';
import {
  CUSTOM_ROLES_IMPORT_EMPTY_FILE,
  CUSTOM_ROLES_IMPORT_MISSING_DESCRIPTION,
  CUSTOM_ROLES_IMPORT_MISSING_PERMISSIONS,
  CUSTOM_ROLES_IMPORT_MISSING_ROLE_NAME,
  CUSTOM_ROLES_IMPORT_NO_ROLE_ROWS,
} from '../../../../utils/constants';

export type BulkUploadRoleRow = {
  roleUuid: string;
  roleName: string;
  description: string;
  permissions: string[];
};

export type ParsedBulkUploadCsv = {
  roles: BulkUploadRoleRow[];
};

export type BulkUploadRoleExportData = {
  id: string;
  name: string;
  description: string;
  permissions: string[];
};

const ROLE_UUID_COLUMN = 'roleuuid';
const ROLE_NAME_COLUMN = 'rolename';
const DESCRIPTION_COLUMN = 'description';
const PERMISSIONS_COLUMN = 'permissions';

export const BULK_UPLOAD_ROLES_CSV_HEADER =
  'RoleUUID,RoleName,Description,Permissions';

export { normalizePermission };

export const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      fields.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  fields.push(current);
  return fields;
};

const escapeCsvField = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const getColumnIndex = (headers: string[], columnName: string): number =>
  headers.findIndex(
    (header) => header.trim().toLowerCase() === columnName.toLowerCase(),
  );

export const parsePermissionsField = (value: string): string[] => {
  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  return Array.from(
    new Set(
      normalized
        .split(',')
        .map((permission) => normalizePermission(permission))
        .filter(Boolean),
    ),
  );
};

const getMissingColumnError = (headerFields: string[]): string | null => {
  if (getColumnIndex(headerFields, ROLE_NAME_COLUMN) === -1) {
    return CUSTOM_ROLES_IMPORT_MISSING_ROLE_NAME;
  }

  if (getColumnIndex(headerFields, DESCRIPTION_COLUMN) === -1) {
    return CUSTOM_ROLES_IMPORT_MISSING_DESCRIPTION;
  }

  if (getColumnIndex(headerFields, PERMISSIONS_COLUMN) === -1) {
    return CUSTOM_ROLES_IMPORT_MISSING_PERMISSIONS;
  }

  return null;
};

export const parseBulkUploadRolesCsv = (
  csvText: string,
): ParsedBulkUploadCsv => {
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    throw new Error(CUSTOM_ROLES_IMPORT_EMPTY_FILE);
  }

  const headerFields = parseCsvLine(lines[0]).map((field) => field.trim());
  const roleUuidIndex = getColumnIndex(headerFields, ROLE_UUID_COLUMN);
  const roleNameIndex = getColumnIndex(headerFields, ROLE_NAME_COLUMN);
  const descriptionIndex = getColumnIndex(headerFields, DESCRIPTION_COLUMN);
  const permissionsIndex = getColumnIndex(headerFields, PERMISSIONS_COLUMN);

  if (
    roleNameIndex === -1 ||
    descriptionIndex === -1 ||
    permissionsIndex === -1
  ) {
    throw new Error(
      getMissingColumnError(headerFields) || 'Invalid CSV format.',
    );
  }

  const roles: BulkUploadRoleRow[] = [];

  lines.slice(1).forEach((line) => {
    const fields = parseCsvLine(line);
    const roleName = (fields[roleNameIndex] || '').trim();
    const description = (fields[descriptionIndex] || '').trim();
    const permissions = parsePermissionsField(fields[permissionsIndex] || '');

    if (!roleName) {
      return;
    }

    roles.push({
      roleUuid:
        roleUuidIndex === -1 ? '' : (fields[roleUuidIndex] || '').trim(),
      roleName,
      description,
      permissions,
    });
  });

  if (roles.length === 0) {
    throw new Error(CUSTOM_ROLES_IMPORT_NO_ROLE_ROWS);
  }

  return { roles };
};

export const buildBulkUploadRolesCsv = (
  roles: BulkUploadRoleExportData[],
): string => {
  const rows = roles.map((role) => {
    const permissionsValue = role.permissions
      .map((permission) => normalizePermission(permission))
      .join(',');

    return [
      escapeCsvField(role.id),
      escapeCsvField(role.name),
      escapeCsvField(role.description),
      escapeCsvField(permissionsValue),
    ].join(',');
  });

  return [BULK_UPLOAD_ROLES_CSV_HEADER, ...rows].join('\n');
};

export const getBulkUploadTemplateCsv = (): string =>
  `${BULK_UPLOAD_ROLES_CSV_HEADER}\n`;

export const downloadCsvContent = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
